import React, { useMemo } from 'react';
import styles from './FloatingImages.module.css';

/* ─── Card layout configs ────────────────────────────────
   Mirrored from the muse-app splash screen.
   Positions are in % of the hero container, anchored from centre.
   Sizes are in vmin for responsive scaling.
   Only transform & opacity are animated → GPU-composited, zero layout cost.
   ──────────────────────────────────────────────────────── */
interface CardLayout {
  x: string; // CSS left (% from centre)
  y: string; // CSS top (% from centre)
  rotate: number; // degrees
  width: string; // CSS width
  delay: number; // entrance stagger (s)
  driftDuration: number; // ambient drift cycle (s)
  driftX: number; // px drift range on X
  driftY: number; // px drift range on Y
}

const CARD_LAYOUTS: CardLayout[] = [
  // Centre-anchored via translate(-50%,-50%) in CSS keyframes.
  // Outer ring pushed toward edges to fill the viewport; inner accents
  // sit behind the hero text for depth.

  // ── 1 · Top-left corner ──
  {
    x: '10%',
    y: '14%',
    rotate: -12,
    width: 'clamp(80px, 10vw, 150px)',
    delay: 0.15,
    driftDuration: 7.2,
    driftX: 5,
    driftY: 8,
  },
  // ── 2 · Top, left-of-centre ──
  {
    x: '35%',
    y: '10%',
    rotate: 5,
    width: 'clamp(72px, 9vw, 135px)',
    delay: 0.5,
    driftDuration: 8.6,
    driftX: 3,
    driftY: 6,
  },
  // ── 3 · Top, right-of-centre ──
  {
    x: '66%',
    y: '12%',
    rotate: -4,
    width: 'clamp(68px, 8.5vw, 128px)',
    delay: 0.7,
    driftDuration: 8.1,
    driftX: 4,
    driftY: 5,
  },
  // ── 4 · Top-right corner ──
  {
    x: '90%',
    y: '18%',
    rotate: 10,
    width: 'clamp(76px, 9.5vw, 142px)',
    delay: 0.4,
    driftDuration: 7.6,
    driftX: 4,
    driftY: 9,
  },
  // ── 5 · Left edge, mid ──
  {
    x: '8%',
    y: '46%',
    rotate: -9,
    width: 'clamp(66px, 8vw, 122px)',
    delay: 0.3,
    driftDuration: 9,
    driftX: 4,
    driftY: 5,
  },
  // ── 6 · Right edge, mid ──
  {
    x: '92%',
    y: '52%',
    rotate: 7,
    width: 'clamp(70px, 8.5vw, 128px)',
    delay: 0.6,
    driftDuration: 7.8,
    driftX: 3,
    driftY: 7,
  },
  // ── 7 · Bottom-left corner ──
  {
    x: '11%',
    y: '78%',
    rotate: 14,
    width: 'clamp(74px, 9vw, 138px)',
    delay: 0.45,
    driftDuration: 7.4,
    driftX: 5,
    driftY: 6,
  },
  // ── 8 · Bottom, left-of-centre ──
  {
    x: '36%',
    y: '88%',
    rotate: -6,
    width: 'clamp(64px, 7.5vw, 118px)',
    delay: 0.8,
    driftDuration: 8.8,
    driftX: 4,
    driftY: 7,
  },
  // ── 9 · Bottom, right-of-centre ──
  {
    x: '65%',
    y: '86%',
    rotate: 8,
    width: 'clamp(70px, 8.5vw, 130px)',
    delay: 0.35,
    driftDuration: 8.3,
    driftX: 3,
    driftY: 6,
  },
  // ── 10 · Bottom-right corner ──
  {
    x: '89%',
    y: '76%',
    rotate: -10,
    width: 'clamp(76px, 9.5vw, 140px)',
    delay: 0.55,
    driftDuration: 7.1,
    driftX: 5,
    driftY: 8,
  },
  // ── 11 · Inner accent, centre-right — peeks behind text ──
  {
    x: '72%',
    y: '38%',
    rotate: -3,
    width: 'clamp(56px, 6.5vw, 100px)',
    delay: 0.9,
    driftDuration: 9.2,
    driftX: 3,
    driftY: 4,
  },
  // ── 12 · Inner accent, centre-left — peeks behind text ──
  {
    x: '28%',
    y: '60%',
    rotate: 6,
    width: 'clamp(54px, 6vw, 96px)',
    delay: 0.75,
    driftDuration: 9.4,
    driftX: 3,
    driftY: 5,
  },
];

const HERO_IMAGES = [
  '/hero-images/muse-app-1.webp',
  '/hero-images/muse-app-3.webp',
  '/hero-images/muse-app-4.webp',
  '/hero-images/muse-app-6.webp',
  '/hero-images/muse-app-7.webp',
  '/hero-images/muse-app-8.webp',
  '/hero-images/muse-app-9.webp',
  '/hero-images/muse-app-10.webp',
  '/hero-images/muse-app-11.webp',
  '/hero-images/muse-app-12.webp',
  '/hero-images/muse-app-13.webp',
  '/hero-images/muse-app-14.webp',
];

/** Single floating card — pure CSS animation, zero JS per-frame cost. */
const FloatingCard: React.FC<{
  src: string;
  layout: CardLayout;
  startDelay: number;
  eager?: boolean;
}> = React.memo(({ src, layout, startDelay, eager = false }) => {
  const totalDelay = startDelay + layout.delay;
  const style: React.CSSProperties = {
    left: layout.x,
    top: layout.y,
    width: layout.width,
    '--card-rotate': `${layout.rotate}deg`,
    '--entrance-delay': `${totalDelay}s`,
    '--drift-duration': `${layout.driftDuration}s`,
    '--drift-x': `${layout.driftX}px`,
    '--drift-y': `${layout.driftY}px`,
  } as React.CSSProperties;

  return (
    <div className={styles.card} style={style} aria-hidden="true">
      <img
        src={src}
        alt=""
        className={styles.cardImage}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        width={160}
        height={216}
      />
    </div>
  );
});

FloatingCard.displayName = 'FloatingCard';

interface FloatingImagesProps {
  /** Seconds to wait before cards start animating in (e.g. after hero text). */
  startDelay?: number;
}

/** Container of all floating image cards behind the hero content. */
const FloatingImages: React.FC<FloatingImagesProps> = ({ startDelay = 0 }) => {
  const cards = useMemo(
    () =>
      CARD_LAYOUTS.map((layout, i) => (
        <FloatingCard
          key={i}
          src={HERO_IMAGES[i % HERO_IMAGES.length]}
          layout={layout}
          startDelay={startDelay}
          eager={i < 4}
        />
      )),
    [startDelay]
  );

  return (
    <div className={styles.container} aria-hidden="true">
      {cards}
    </div>
  );
};

export default React.memo(FloatingImages);
